import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Facebook, Instagram, Loader2, Mail } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { BRAND, INQUIRY_TYPES } from "@/lib/site";

const inputCls =
    "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-silver placeholder:text-fog/50 outline-none transition-colors focus:border-neon-pink";
const labelCls =
    "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-fog";

export default function Contact() {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState({
        name: "",
        email: "",
        inquiry_type: INQUIRY_TYPES.includes(searchParams.get("type"))
            ? searchParams.get("type")
            : INQUIRY_TYPES[0],
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await submitContact(form);
            setSubmitted(true);
            toast.success("Message received — you're checked in!");
        } catch (err) {
            toast.error(
                "Couldn't send your message. Please try again in a moment.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Say hello"
                title="Contact"
                scriptWord="let's check in!"
                description="Get in touch, submit a story idea, or request an interview — Courtnie reads every message herself."
            />

            <section className="pb-24 sm:pb-32">
                <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="eyebrow">Direct lines</p>
                            <div className="mt-8 space-y-6" data-testid="contact-direct-info">
                                <a
                                    data-testid="contact-email-link"
                                    href={`mailto:${BRAND.email}`}
                                    className="flex items-center gap-4 border border-white/10 bg-coal/50 p-5 transition-colors hover:border-neon-pink/50"
                                >
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 text-neon-pink">
                                        <Mail size={16} />
                                    </span>
                                    <span>
                                        <span className="block text-xs text-fog">
                                            Email
                                        </span>
                                        <span className="block text-sm text-silver">
                                            {BRAND.email}
                                        </span>
                                    </span>
                                </a>
                                <div className="grid grid-cols-2 gap-4">
                                    <a
                                        data-testid="contact-facebook-link"
                                        href={BRAND.facebookUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex flex-col items-center gap-2 border border-white/10 bg-coal/50 p-5 transition-colors hover:border-neon-pink/50"
                                    >
                                        <Facebook
                                            size={18}
                                            className="text-neon-pink"
                                        />
                                        <span className="text-xs text-fog">
                                            Facebook
                                        </span>
                                        <span className="text-[11px] text-silver">
                                            {BRAND.facebook}
                                        </span>
                                    </a>
                                    <a
                                        data-testid="contact-instagram-link"
                                        href={BRAND.instagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex flex-col items-center gap-2 border border-white/10 bg-coal/50 p-5 transition-colors hover:border-neon-pink/50"
                                    >
                                        <Instagram
                                            size={18}
                                            className="text-neon-pink"
                                        />
                                        <span className="text-xs text-fog">
                                            Instagram
                                        </span>
                                        <span className="text-[11px] text-silver">
                                            {BRAND.instagram}
                                        </span>
                                    </a>
                                </div>
                                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-fog">
                                    Based in {BRAND.city} · Real talk only
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            {submitted ? (
                                <div
                                    data-testid="contact-success-alert"
                                    className="flex h-full flex-col items-center justify-center border border-neon-pink/40 bg-coal/60 p-10 text-center sm:p-16"
                                >
                                    <span className="flex h-16 w-16 items-center justify-center rounded-full border border-neon-pink text-neon-pink neon-box-pink">
                                        <Check size={28} />
                                    </span>
                                    <p className="mt-8 font-script text-4xl text-neon-pink neon-pink-text">
                                        you're checked in!
                                    </p>
                                    <h2 className="mt-4 font-display text-2xl font-bold uppercase">
                                        Message received
                                    </h2>
                                    <p className="mt-4 max-w-md text-fog">
                                        Thanks for reaching out. Your message
                                        landed in Courtnie's inbox — expect a
                                        reply when she's back from the field.
                                    </p>
                                    <button
                                        data-testid="contact-reset-button"
                                        onClick={() => {
                                            setSubmitted(false);
                                            setForm({
                                                name: "",
                                                email: "",
                                                inquiry_type: INQUIRY_TYPES[0],
                                                message: "",
                                            });
                                        }}
                                        className="mt-8 rounded-full border border-white/20 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-silver transition-all hover:border-neon-pink hover:text-neon-pink"
                                    >
                                        Send another message
                                    </button>
                                </div>
                            ) : (
                                <form
                                    data-testid="contact-form"
                                    onSubmit={onSubmit}
                                    className="border border-white/10 bg-coal/60 p-6 sm:p-10"
                                >
                                    <div className="grid gap-6 sm:grid-cols-2">
                                        <div>
                                            <label
                                                htmlFor="c-name"
                                                className={labelCls}
                                            >
                                                Your name
                                            </label>
                                            <input
                                                id="c-name"
                                                data-testid="contact-name-input"
                                                required
                                                value={form.name}
                                                onChange={set("name")}
                                                placeholder="First & last"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="c-email"
                                                className={labelCls}
                                            >
                                                Email
                                            </label>
                                            <input
                                                id="c-email"
                                                data-testid="contact-email-input"
                                                required
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                placeholder="you@example.com"
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-6">
                                        <label
                                            htmlFor="c-type"
                                            className={labelCls}
                                        >
                                            What's this about?
                                        </label>
                                        <select
                                            id="c-type"
                                            data-testid="contact-type-select"
                                            value={form.inquiry_type}
                                            onChange={set("inquiry_type")}
                                            className={`${inputCls} appearance-none`}
                                        >
                                            {INQUIRY_TYPES.map((t) => (
                                                <option
                                                    key={t}
                                                    value={t}
                                                    className="bg-ink"
                                                >
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mt-6">
                                        <label
                                            htmlFor="c-message"
                                            className={labelCls}
                                        >
                                            Your message
                                        </label>
                                        <textarea
                                            id="c-message"
                                            data-testid="contact-message-input"
                                            required
                                            rows={6}
                                            value={form.message}
                                            onChange={set("message")}
                                            placeholder="Tell us the story, the idea, or who we should check in with..."
                                            className={`${inputCls} resize-none`}
                                        />
                                    </div>
                                    <button
                                        data-testid="contact-submit-button"
                                        type="submit"
                                        disabled={submitting}
                                        className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-neon-pink px-7 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink transition-all hover:neon-box-pink disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2
                                                    size={15}
                                                    className="animate-spin"
                                                />
                                                Sending...
                                            </>
                                        ) : (
                                            "Send it"
                                        )}
                                    </button>
                                </form>
                            )}
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
}
